import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { reviewsData } from '../data/reviews';
import { Star, Quote } from 'lucide-react';

const Reviews = () => {
  const { language } = useLanguage();
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  const reviews = reviewsData[language];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let scrollInterval;
    const scroll = () => {
      if (!isPaused && scrollContainer) {
        scrollContainer.scrollLeft += 1;
        
        // Reset scroll when reaching the end
        if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth - scrollContainer.clientWidth) {
          scrollContainer.scrollLeft = 0;
        }
      }
    };

    scrollInterval = setInterval(scroll, 30);

    return () => clearInterval(scrollInterval);
  }, [isPaused]);

  return (
    <section id="reviews" className="py-24 bg-[#0f172a] overflow-hidden">
      <div className="container mx-auto px-8">
        <div className="text-center mb-16">
          <h2 className="heading-2 text-[#60a5fa] mb-4">
            {language === 'en' ? 'CLIENT REVIEWS' : 'KLANTBEOORDELINGEN'}
          </h2>
          <p className="body-large text-[#94a3b8] max-w-3xl mx-auto">
            {language === 'en' 
              ? 'See what our clients say about working with Charter Roofings'
              : 'Zie wat onze klanten zeggen over het werken met Charter Roofings'}
          </p>
          <div className="flex items-center justify-center space-x-2 mt-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-[#fbbf24] fill-current" />
              ))}
            </div>
            <span className="body-medium text-[#60a5fa] font-semibold">9.2</span>
            <span className="body-small text-[#94a3b8]">
              {language === 'en' ? 'out of 10 - Based on Customer Reviews' : 'van de 10 - Op basis van Klantbeoordelingen'}
            </span>
          </div>
        </div>

        {/* Scrolling Reviews Container */}
        <div 
          ref={scrollRef}
          className="flex gap-6 overflow-x-hidden pb-4"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          style={{ scrollBehavior: 'auto' }}
        >
          {/* Duplicate reviews for infinite scroll effect */}
          {[...reviews, ...reviews].map((review, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-[400px] bg-[#1e293b] border border-[#1e40af] p-6 rounded-lg hover:border-[#60a5fa] transition-all duration-300 hover:shadow-lg hover:shadow-[#60a5fa]/20"
            >
              {/* Rating */}
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-[#fbbf24] fill-current" />
                ))}
              </div>

              {/* Quote Icon */}
              <Quote className="w-8 h-8 text-[#1e40af] mb-3" />

              {/* Review Text */}
              <p className="body-small text-[#94a3b8] mb-4 italic leading-relaxed">
                "{review.text}"
              </p>

              {/* Project Info */}
              <div className="mb-4 pt-3 border-t border-[#1e40af]">
                <p className="caption text-[#60a5fa] font-semibold">
                  {review.project}
                </p>
              </div>

              {/* Reviewer Info */}
              <div className="flex items-start justify-between">
                <div>
                  <p className="body-small text-[#60a5fa] font-semibold">{review.name}</p>
                  <p className="caption text-[#94a3b8]">{review.company}</p>
                </div>
                <span className="caption text-[#94a3b8]">{review.date}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Hover hint */}
        <div className="text-center mt-8">
          <p className="caption text-[#94a3b8]">
            {language === 'en' 
              ? 'Hover over reviews to pause scrolling'
              : 'Beweeg over beoordelingen om scrollen te pauzeren'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
